/**
 * summarize-session.ts — session result summary (Phase 7 scoring, task #3).
 *
 * `summarizeSession` folds a stream of per-frame verdicts (each tagged with its
 * SONG-timeline position) into one deterministic `SessionResult`: overall
 * accuracy, in-tune ratio, cents stats, per-note breakdown, and unstable
 * sections. It is pure — same input ⇒ same output, no React/native dependency.
 *
 * Invariants honoured here (GOAL.md "NEVER VIOLATE"):
 *   - Scoring is built on raw-f0 verdicts (`FrameScore` from `scoreFrame`); no
 *     display-smoothed values enter the summary.
 *   - silent (no target → resting) and missed (target active, not singing) are
 *     treated differently: `missed` counts against accuracy, `silent` never does.
 *   - Discontinuity frames (`stream_discontinuity`, BT down-weight — see
 *     docs/native/native-timebase-and-latency.md §5–6) are weighted down so a
 *     glitch cannot tank the score or fabricate an unstable section.
 *   - `atMs` is the song-timeline position; the capture→song mapping is applied
 *     UPSTREAM (native/scoring layer), so this core stays clock-free.
 */

import type { FrameScore } from './score-frame';
import type { TargetMelody, TargetNote } from './target-melody';

/** One scored frame on the song timeline, ready to summarise. */
export type ScoredFrame = {
  /** Song-timeline position (aligned upstream), milliseconds. */
  atMs: number;
  /** Verdict from `scoreFrame` for this frame. */
  score: FrameScore;
  /** Frame lost stream continuity (`frame.continuity?.discontinuity`). */
  discontinuity?: boolean;
};

export type PerNoteResult = {
  /** Index into `melody.notes`. */
  index: number;
  midi: number;
  startMs: number;
  durMs: number;
  lyric?: string;
  /** Raw (unweighted) frames that landed on this note. */
  sampleCount: number;
  /** in_tune / voiced-judged frames within the note (0..1). */
  inTuneRatio: number;
  /** Weighted mean of |cents| over voiced-judged frames, or null. */
  meanAbsCents: number | null;
  verdict: 'hit' | 'partial' | 'off' | 'missed' | 'untested';
};

export type SessionResult = {
  /** in_tune / judged (judged includes missed); 0..100. */
  accuracyPct: number;
  /** in_tune / voiced-judged (excludes missed); 0..1. */
  inTuneRatio: number;
  /** Weighted mean of |cents| over voiced-judged frames, or null. */
  meanAbsCents: number | null;
  /** Weighted std of signed cents over voiced-judged frames, or null. */
  centsStd: number | null;
  perNote: PerNoteResult[];
  /** Contiguous song-time spans where the user was off (high/low/missed). */
  unstableSections: { startMs: number; endMs: number }[];
  /** Total covered duration = last note end, milliseconds. */
  durationMs: number;
};

export type SummarizeOptions = {
  /** Relative weight (0..1) of frames flagged with a stream discontinuity. */
  discontinuityWeight: number;
  /** Minimum contiguous bad-run length (frames) to record an unstable section. */
  minUnstableRun: number;
  /** Per-note in-tune ratio at/above which the note counts as `hit`. */
  noteHitRatio: number;
};

export const DEFAULT_SUMMARIZE_OPTIONS: SummarizeOptions = {
  discontinuityWeight: 0.25,
  minUnstableRun: 2,
  noteHitRatio: 0.5,
};

// ── weighted accumulator ────────────────────────────────────────────────────

type Acc = {
  /** in_tune + high + low + missed. */
  judgedW: number;
  inTuneW: number;
  /** in_tune + high + low (frames with a usable cents reading). */
  voicedJudgedW: number;
  absCentsW: number;
  centsW: number;
  centsSqW: number;
  centsWeight: number;
  /** Raw, unweighted frame count. */
  count: number;
};

const emptyAcc = (): Acc => ({
  judgedW: 0,
  inTuneW: 0,
  voicedJudgedW: 0,
  absCentsW: 0,
  centsW: 0,
  centsSqW: 0,
  centsWeight: 0,
  count: 0,
});

const weightOf = (s: ScoredFrame, opts: SummarizeOptions): number =>
  s.discontinuity ? opts.discontinuityWeight : 1;

const addSample = (acc: Acc, s: ScoredFrame, w: number): void => {
  acc.count += 1;
  const v = s.score.verdict;
  if (v === 'silent' || v === 'unknown') return; // never penalise resting / uncertain
  acc.judgedW += w;
  if (v === 'missed') return; // judged but no pitch to read
  acc.voicedJudgedW += w;
  if (v === 'in_tune') acc.inTuneW += w;
  const c = s.score.centsDiff;
  if (c === null) return;
  acc.absCentsW += w * Math.abs(c);
  acc.centsW += w * c;
  acc.centsSqW += w * c * c;
  acc.centsWeight += w;
};

const accuracyOf = (a: Acc): number =>
  a.judgedW > 0 ? (a.inTuneW / a.judgedW) * 100 : 0;

const inTuneRatioOf = (a: Acc): number =>
  a.voicedJudgedW > 0 ? a.inTuneW / a.voicedJudgedW : 0;

const meanAbsCentsOf = (a: Acc): number | null =>
  a.centsWeight > 0 ? a.absCentsW / a.centsWeight : null;

const centsStdOf = (a: Acc): number | null => {
  if (a.centsWeight <= 0) return null;
  const mean = a.centsW / a.centsWeight;
  const variance = a.centsSqW / a.centsWeight - mean * mean;
  return Math.sqrt(Math.max(0, variance));
};

// ── per-note grouping ───────────────────────────────────────────────────────

/** Index of the first note whose [startMs, startMs+durMs) span contains atMs. */
const noteIndexAt = (notes: TargetNote[], atMs: number): number => {
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (atMs < n.startMs) return -1; // sorted → no later note can match
    if (atMs < n.startMs + n.durMs) return i;
  }
  return -1;
};

const noteVerdict = (a: Acc, hitRatio: number): PerNoteResult['verdict'] => {
  if (a.judgedW <= 0) return 'untested';
  if (a.voicedJudgedW <= 0) return 'missed';
  const ratio = inTuneRatioOf(a);
  if (ratio >= hitRatio) return 'hit';
  if (ratio > 0) return 'partial';
  return 'off';
};

const toPerNote = (
  note: TargetNote,
  index: number,
  acc: Acc,
  hitRatio: number,
): PerNoteResult => {
  const result: PerNoteResult = {
    index,
    midi: note.midi,
    startMs: note.startMs,
    durMs: note.durMs,
    sampleCount: acc.count,
    inTuneRatio: inTuneRatioOf(acc),
    meanAbsCents: meanAbsCentsOf(acc),
    verdict: noteVerdict(acc, hitRatio),
  };
  if (note.lyric !== undefined) result.lyric = note.lyric;
  return result;
};

// ── unstable sections ───────────────────────────────────────────────────────

/** A real off-pitch frame: judged-not-in-tune, and not a down-weighted glitch. */
const isUnstable = (s: ScoredFrame): boolean => {
  if (s.discontinuity) return false;
  const v = s.score.verdict;
  return v === 'high' || v === 'low' || v === 'missed';
};

const findUnstableSections = (
  sorted: ScoredFrame[],
  minRun: number,
): { startMs: number; endMs: number }[] => {
  const sections: { startMs: number; endMs: number }[] = [];
  let run: ScoredFrame[] = [];
  const flush = (): void => {
    if (run.length >= minRun) {
      sections.push({
        startMs: run[0].atMs,
        endMs: run[run.length - 1].atMs,
      });
    }
    run = [];
  };
  for (const s of sorted) {
    if (isUnstable(s)) run.push(s);
    else flush();
  }
  flush();
  return sections;
};

const totalDurationMs = (notes: TargetNote[]): number =>
  notes.reduce((max, n) => Math.max(max, n.startMs + n.durMs), 0);

// ── public API ──────────────────────────────────────────────────────────────

/**
 * Summarise a session's scored frames against the target melody.
 * Pure/deterministic: same (frames, melody, opts) ⇒ same SessionResult.
 */
export const summarizeSession = (
  frames: ScoredFrame[],
  melody: TargetMelody,
  opts: SummarizeOptions = DEFAULT_SUMMARIZE_OPTIONS,
): SessionResult => {
  const total = emptyAcc();
  const perNoteAcc = melody.notes.map(emptyAcc);

  for (const s of frames) {
    const w = weightOf(s, opts);
    addSample(total, s, w);
    const idx = noteIndexAt(melody.notes, s.atMs);
    if (idx >= 0) addSample(perNoteAcc[idx], s, w);
  }

  const sorted = [...frames].sort((a, b) => a.atMs - b.atMs);

  return {
    accuracyPct: accuracyOf(total),
    inTuneRatio: inTuneRatioOf(total),
    meanAbsCents: meanAbsCentsOf(total),
    centsStd: centsStdOf(total),
    perNote: melody.notes.map((n, i) =>
      toPerNote(n, i, perNoteAcc[i], opts.noteHitRatio),
    ),
    unstableSections: findUnstableSections(sorted, opts.minUnstableRun),
    durationMs: totalDurationMs(melody.notes),
  };
};
